/**
 * OmniLeagues — Public password-reset landing (/reset-password?token=…)
 *
 * The forgot-password email links here. The member sets a new password
 * (with confirm + policy hint) which calls POST /api/auth/reset-password
 * via AuthContext.resetPassword. On success we bounce to /login with a
 * toast; a missing/expired token shows a clear message.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const POLICY_HINT = 'At least 8 characters with one uppercase letter, one lowercase letter and one number.';

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [form, setForm] = useState({ next: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputCls =
    'bg-transparent border-0 border-b border-slate-800 rounded-none px-0 h-12 text-slate-100 ' +
    'placeholder:text-slate-600 focus-visible:ring-0 focus-visible:border-amber-500 text-base';
  const labelCls = 'text-[11px] tracking-[0.18em] text-slate-500 uppercase';

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('This reset link is missing its token. Please request a new one from the Forgot password page.'); return; }
    if (form.next !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.next.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    const res = await resetPassword(token, form.next);
    setLoading(false);
    if (res.success) {
      toast.success('Password reset. Please sign in with your new password.');
      navigate('/login', { replace: true });
    } else {
      setError(res.error || 'That reset link is invalid or has expired. Please request a new one.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 text-slate-100"
      style={{ backgroundColor: '#0B0B0B' }} data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <div className="text-2xl font-semibold">OmniLeagues</div>
        </div>
        <form onSubmit={submit} className="space-y-6" data-testid="reset-form">
          <div>
            <h1 className="text-3xl tracking-[0.1em] font-bold">SET A NEW PASSWORD</h1>
            <p className="mt-2 text-sm text-slate-400">{POLICY_HINT}</p>
          </div>

          {!token && (
            <div className="flex items-start gap-2 text-sm border rounded-sm px-3 py-2 bg-amber-500/10 border-amber-500/30 text-amber-300" data-testid="reset-no-token">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>This reset link is missing its token. Request a new link from the sign-in page.</span>
            </div>
          )}

          <div>
            <label className={labelCls}>New password</label>
            <Input type="password" required minLength={8} value={form.next}
              onChange={(e) => setForm((p) => ({ ...p, next: e.target.value }))}
              placeholder="New password" className={inputCls} autoComplete="new-password"
              data-testid="reset-new-password" />
          </div>
          <div>
            <label className={labelCls}>Confirm new password</label>
            <Input type="password" required minLength={8} value={form.confirm}
              onChange={(e) => setForm((p) => ({ ...p, confirm: e.target.value }))}
              placeholder="Confirm new password" className={inputCls} autoComplete="new-password"
              data-testid="reset-confirm-password" />
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm border rounded-sm px-3 py-2 bg-red-500/10 border-red-500/30 text-red-300" data-testid="reset-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}

          <Button type="submit" disabled={loading}
            className="w-full h-12 bg-[#1e3a8a] hover:bg-[#1e40af] text-white tracking-[0.18em] rounded-sm text-sm"
            data-testid="reset-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>RESET PASSWORD <ArrowRight className="h-4 w-4 ml-2" /></>}
          </Button>

          <div className="text-center text-sm text-slate-400">
            <button type="button" onClick={() => navigate('/login')}
              className="text-white font-semibold hover:text-amber-400" data-testid="reset-back-to-login">
              Back to sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
